import React, { useEffect, useCallback, useMemo } from "react";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useBaseService from "../../../../hooks/services/useBaseService";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import useAction from "../../../../hooks/useAction";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import useGenericFilter from "../../../../hooks/filters/useGenericFilter";
import { faEdit, faTrashAlt, faUndo } from "@fortawesome/free-solid-svg-icons";
import MainLayout from "../../../../layouts/MainLayout";
import PageHeader from "../../../../components/PageHeader";
import ListHeader from "../../../../components/ListHeader";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import { entities } from "../../../../constants/entities";
import TableWithInactiveFilter from "../../../../components/table/TableWithInactiveFilter";
import FilterCollapse from "../../../../components/FilterCollapse";
import { inputsFiltersGroups } from "./filters/filters-groups";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const GroupPage = () => {
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();

  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate);

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "almox_groups",
  );

  // --- Lógica de busca de grupos com TanStack Query ---
  const {
    data: groupsData,
    isLoading,
    isFetching,
    isError: isErrorGroups,
    error: groupsError,
  } = useQueryWithLoader({
    queryKey: ["almox_groups", filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(entities.groups.get, params);
      return response.result;
    },
    select: (data) => {
      return {
        groups: data.data.map((group) => ({
          id: group.id,
          name: group.name,
          is_in_product: group.is_in_product,
          isDeleted: !!group.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
    placeholderData: (previousData) => previousData,
  });

  // Extrai grupos e totalPages dos dados transformados pelo useQuery
  const { groups = [], totalPages = 1 } = groupsData || {};

  const columns = [{ key: "name", label: "Nome" }];

  // --- Gerenciamento de Loader com base nos estados do TanStack Query ---
  useEffect(() => {
    if (isLoading || isFetching) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoading, isFetching, showLoader, hideLoader]);

  // --- Notificação de Erro para a Query de Grupos ---
  useEffect(() => {
    if (isErrorGroups) {
      console.error(
        "GroupPage: Erro ao buscar grupos (useQuery):",
        groupsError,
      );
      showNotification(
        "error",
        `Erro ao buscar grupos: ${
          groupsError.response?.data?.message || groupsError.message
        }`,
      );
    }
  }, [isErrorGroups, groupsError, showNotification]);

  // --- Função para Forçar o Refetch da Lista (invalida o cache) ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["almox_groups", filters, pagination],
    });
  }, [queryClient, filters, pagination]);

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  // Lógica de Aplicação e Limpeza de Filtros (via useGenericFilter)
  const handleFilterApply = useCallback(
    (currentLocalFilterValues) => {
      const transformed = transformFiltersForUrl(currentLocalFilterValues);
      applyUrlFilters(transformed);
    },
    [applyUrlFilters],
  );

  const handleFilterClear = useCallback(
    (initialLocalFilterState) => {
      const transformedInitial = transformFiltersForUrl(
        initialLocalFilterState,
      );
      console.log(
        "GroupPage: handleFilterClear - Filtros iniciais transformados:",
        JSON.stringify(transformedInitial, null, 2),
      );
      applyUrlFilters(transformedInitial);
    },
    [applyUrlFilters],
  );

  // Inicializa o useGenericFilter com o estado inicial dos filtros do formulário
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        name: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  const inactivateGroupMutation = useMutation({
    mutationFn: async (groupId) => {
      showLoader();
      const response = await baseServiceDelete(entities.groups.delete(groupId));
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        refreshList();
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      hideLoader();
    },
    onSettled: () => {
      handleCancelConfirmation();
    },
  });

  const activateGroupMutation = inactivateGroupMutation;

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      // Limpa o estado para que a mensagem não apareça novamente ao navegar
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const actions = useMemo(
    () => [
      {
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar grupos",
        onClick: (group) =>
          navigate(`/almoxarifado/produtos/grupos/editar/${group.id}`),
        show: (group) => !group.isDeleted && canAccess("Atualizar grupos"),
      },
      {
        id: "delete",
        icon: faTrashAlt,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir grupos",
        onClick: (group) =>
          showConfirmation(
            group,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactivateGroupMutation.mutateAsync(group.id);
              } catch (error) {
                console.log("Erro ao excluir grupo:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (group) =>
          !group.isDeleted &&
          !group.is_in_product &&
          canAccess("Excluir grupos"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir grupos",
        onClick: (group) =>
          showConfirmation(
            group,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateGroupMutation.mutateAsync(group.id);
              } catch (error) {
                console.log("Erro ao restaurar grupo:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (group) => group.isDeleted && canAccess("Excluir grupos"),
      },
    ],
    [
      navigate,
      canAccess,
      showConfirmation,
      inactivateGroupMutation,
      activateGroupMutation,
    ],
  ); // Adicionado as mutações como dependência

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Grupos" showBackButton={true} backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsFiltersGroups({
            localFilterValues,
            handleFilterChange,
          })}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Grupos"
          buttonText="Novo Grupo"
          buttonLink="/almoxarifado/produtos/grupos/criar"
          canAccess={canAccess}
          permission="Criar grupos"
        />

        <TableWithInactiveFilter
          data={groups}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default GroupPage;
